import { create } from 'zustand';

import {
  NotificationState,
  SendNotificationRequest,
  CreateCampaignRequest,
  UpdateCampaignRequest,
  NotificationCampaign,
} from 'src/types/notification';
import { notificationRepository } from 'src/repositories/notifications/notification.repository';

type GetCampaignParams = {
  page?: number;
  perPage?: number;
  orderBy?: string;
  order?: 'asc' | 'desc';
  search?: string;
};

type Actions = {
  setPage: (page: number) => void;
  setRowsPerPage: (rowsPerPage: number) => void;
  setLoading: (loading: boolean) => void;
  clearError: () => void;
  resetTableState: () => void;
  fetchNotifications: () => Promise<void>;
  sendNotification: (data: SendNotificationRequest) => Promise<void>;
  markAsViewed: (notificationId: string) => Promise<void>;
  checkUnread: () => Promise<void>;
  createCampaign: (data: CreateCampaignRequest) => Promise<string>; // Returns campaign_id
  fetchCampaigns: (params?: GetCampaignParams) => Promise<void>;
  getCampaignById: (id: string) => Promise<NotificationCampaign>;
  updateCampaign: (id: string, data: UpdateCampaignRequest) => Promise<void>;
  deleteCampaign: (id: string) => Promise<void>;
};

const initialValues: NotificationState = {
  notifications: [],
  campaigns: [],
  loading: false,
  error: null,
  page: 0,
  rowsPerPage: 10,
  totalCount: 0,
  hasUnread: false,
};

export const useNotificationStore = create<NotificationState & Actions>((set, get) => ({
  ...initialValues,

  setPage: (page: number) => set({ page }),

  setRowsPerPage: (rowsPerPage: number) => set({ rowsPerPage }),

  setLoading: (loading: boolean) => set({ loading }),

  clearError: () => set({ error: null }),

  resetTableState: () => set({ page: 0, rowsPerPage: 10 }),

  /**
   * Busca todas as notificações (endpoint admin)
   */
  fetchNotifications: async () => {
    try {
      set({ loading: true, error: null });
      const notifications = await notificationRepository.getAll();
      const notificationsArray = Array.isArray(notifications) ? notifications : [];
      set({
        notifications: notificationsArray,
        totalCount: notificationsArray.length,
        loading: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Erro ao carregar notificações',
        notifications: [],
        loading: false,
      });
    }
  },

  /**
   * Envia notificação para usuário específico
   */
  sendNotification: async (data: SendNotificationRequest) => {
    try {
      set({ loading: true, error: null });
      await notificationRepository.sendNotification(data);
      set({ loading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Erro ao enviar notificação',
        loading: false,
      });
      throw error;
    }
  },

  /**
   * Marca notificação como visualizada
   */
  markAsViewed: async (notificationId: string) => {
    try {
      set({ loading: true, error: null });
      await notificationRepository.markAsViewed(notificationId);

      // Atualiza a notificação localmente
      const { notifications } = get();
      const updatedNotifications = notifications.map((notif) =>
        notif.id === notificationId ? { ...notif, viewed: true, viewedAt: new Date() } : notif
      );

      set({
        notifications: updatedNotifications,
        loading: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Erro ao marcar notificação como lida',
        loading: false,
      });
      throw error;
    }
  },

  /**
   * Verifica se há notificações não lidas
   */
  checkUnread: async () => {
    try {
      const hasUnread = await notificationRepository.hasUnread();
      set({ hasUnread });
    } catch (error) {
      console.error('Erro ao verificar notificações não lidas:', error);
    }
  },

  /**
   * Cria e envia campanha de notificação
   * @returns O ID da campanha criada
   */
  createCampaign: async (data: CreateCampaignRequest) => {
    try {
      set({ loading: true, error: null });
      const response = await notificationRepository.createCampaign(data);
      set({ loading: false });
      // Recarrega as campanhas após criar uma nova
      get().fetchCampaigns();
      return response.campaignId;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Erro ao criar campanha',
        loading: false,
      });
      throw error;
    }
  },

  /**
   * Busca todas as campanhas com paginação
   */
  fetchCampaigns: async (params?: GetCampaignParams) => {
    try {
      set({ loading: true, error: null });
      const currentState = get();
      const queryParams = {
        page: params?.page ?? currentState.page + 1, // API usa 1-based index
        perPage: params?.perPage ?? currentState.rowsPerPage,
        orderBy: params?.orderBy || 'createdAt',
        order: params?.order || 'desc',
        search: params?.search,
      };

      const response = await notificationRepository.getCampaigns(queryParams);
      set({
        campaigns: response.items,
        totalCount: response.pagination.totalCount,
        loading: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Erro ao carregar campanhas',
        campaigns: [],
        loading: false,
      });
    }
  },

  /**
   * Busca uma campanha específica por ID
   */
  getCampaignById: async (id: string) => {
    try {
      set({ loading: true, error: null });
      const campaign = await notificationRepository.getCampaignById(id);
      set({ loading: false });
      return campaign;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Erro ao carregar campanha',
        loading: false,
      });
      throw error;
    }
  },

  /**
   * Atualiza uma campanha existente
   */
  updateCampaign: async (id: string, data: UpdateCampaignRequest) => {
    try {
      set({ loading: true, error: null });
      await notificationRepository.updateCampaign(id, data);
      set({ loading: false });
      // Recarrega as campanhas após atualizar
      get().fetchCampaigns();
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Erro ao atualizar campanha',
        loading: false,
      });
      throw error;
    }
  },

  /**
   * Deleta uma campanha
   */
  deleteCampaign: async (id: string) => {
    try {
      set({ loading: true, error: null });
      await notificationRepository.deleteCampaign(id);
      set({ loading: false });
      // Recarrega as campanhas após deletar
      get().fetchCampaigns();
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Erro ao deletar campanha',
        loading: false,
      });
      throw error;
    }
  },
}));
